import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Table, Skeleton, Input, Space, Form, message, Button, } from 'antd';
import type { ColumnsType, SorterResult, FilterValue, TableCurrentDataSource, TablePaginationConfig, } from 'antd/es/table/interface';
import FormButton from '../components/common/formbutton';
import StoreDetailsDrawer from '../components/stores/storedetailsdrawer';
import { getLanguagesForStore, getPagedStores, getTraitsForStore, updateStoreDetails, } from '../services/storeserivce';
import type { Store, StoreFormValues, SortInfo, PagedStoresResponse, } from '../types/storetypes';
import { getActiveLanguages } from '../services/languageservice';
import { LanguageDto } from '../types/languagetypes';
import { Currency } from '../types/currencytypes';
import { getActiveCurrencies } from '../services/currencyservice';
import AddStoreDrawer from '../components/stores/addstoredrawer';
import { useAuth } from '../auth/authcontext';
import { PlusOutlined } from "@ant-design/icons";

const { Search } = Input;

const Stores: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [stores, setStores] = useState<Store[]>([]);
    const [messageApi, contextHolder] = message.useMessage();
    const [saveLoading, setSaveLoading] = useState(false);

    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalStores, setTotalStores] = useState(0);
    const [sortedInfo, setSortedInfo] = useState<SortInfo>({});

    const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
    const [selectedStore, setSelectedStore] = useState<Store | null>(null);
    const [storeDetailsForm] = Form.useForm<StoreFormValues>();
    const [activeLanguages, setActiveLanguages] = useState<LanguageDto[]>([]);
    const [activeCurrencies, setActiveCurrencies] = useState<Currency[]>([]);
    const [addDrawerOpen, setAddDrawerOpen] = useState(false);
    const { userId } = useAuth();
    const createdByGuid = userId || '';

    const fetchStores = useCallback(async () => {
        setLoading(true);
        try {
            const params: Record<string, any> = {
                pageNumber: currentPage,
                pageSize,
                ...(searchText && { searchText }),
            };

            if (sortedInfo.columnKey && sortedInfo.order) {
                params.sortBy = sortedInfo.columnKey;
                params.sortOrder = sortedInfo.order === 'ascend' ? 'asc' : 'desc';
            }

            const data: PagedStoresResponse = await getPagedStores(params);
            setStores(data.data);
            setTotalStores(data.totalCount);
        } catch (error) {
            console.error('Error fetching stores with pagination:', error);
            messageApi.error('Failed to load stores. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchText, sortedInfo, messageApi]);

    useEffect(() => {
        fetchStores();
    }, [fetchStores]);

    const handleTableChange = (
        pagination: TablePaginationConfig,
        _filters: Record<string, FilterValue | null>,
        sorter: SorterResult<Store> | SorterResult<Store>[],
        _extra: TableCurrentDataSource<Store>
    ) => {
        if (pagination.current !== currentPage) {
            setCurrentPage(pagination.current || 1);
        }
        if (pagination.pageSize !== pageSize) {
            setPageSize(pagination.pageSize || 10);
            setCurrentPage(1);
        }

        if (!Array.isArray(sorter) && sorter.columnKey && sorter.order) {
            setSortedInfo({ columnKey: sorter.columnKey as string, order: sorter.order });
        } else {
            setSortedInfo({});
        }
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
        setCurrentPage(1);
    };

    const handleDetails = async (store: Store) => {
        setSelectedStore(store);
        setDetailsDrawerVisible(true);
        storeDetailsForm.resetFields();

        try {
            const storeTraits = await getTraitsForStore(store.guid);
            const storeLanguages = await getLanguagesForStore(store.guid);
            const activeLanguagesResponse = await getActiveLanguages();
            const activeCurrenciesResponse = await getActiveCurrencies();
            setActiveLanguages(activeLanguagesResponse.data);
            setActiveCurrencies(activeCurrenciesResponse);

            storeDetailsForm.setFieldsValue({
                allowOutOfStock: store.allowOutOfStock,
                restockTime: store.restockTime,
                kiosks: store.kiosks,
                traits: storeTraits,
                supportedLanguages:
                    storeLanguages?.supportedLanguageGuids ?? [],
                primaryLanguage:
                    storeLanguages?.primaryLanguageGuid ?? null,
                currency: store.currencyGuid,
            });
        } catch {
            messageApi.error('Failed to load store traits.');
        }
    };

    const closeDetailsDrawer = () => {
        setSelectedStore(null);
        setDetailsDrawerVisible(false);
    };

    const saveDetails = async () => {
        try {
            const values: StoreFormValues =
                await storeDetailsForm.validateFields();

            setSaveLoading(true);

            const storeUpdateData = {
                storeGuid: selectedStore?.guid,
                allowOutOfStock: values.allowOutOfStock,
                restockTime: values.restockTime,
                kiosks: values.kiosks,
                traits: values.traits,
                languages: {
                    supportedLanguageGuids: values.supportedLanguages ?? [],
                    primaryLanguageGuid: values.primaryLanguage ?? null,
                },
                currencyGuid: values.currency ?? null
            };

            await updateStoreDetails(storeUpdateData);

            messageApi.success("Store details updated successfully.");
            fetchStores();
            setDetailsDrawerVisible(false);
            setSelectedStore(null);
        } catch (error: any) {
            // VALIDATION error
            if (error?.errorFields) {
                return;
            }

            // API error
            const backendMessage =
                error?.response?.data?.message ||
                error?.response?.data ||
                error?.message ||
                "Failed to update store details.";

            messageApi.error(
                typeof backendMessage === "string"
                    ? backendMessage
                    : JSON.stringify(backendMessage)
            );

            console.error("Save store failed:", error);
        } finally {
            setSaveLoading(false);
        }
    };

    const columns: ColumnsType<Store> = [
        {
            title: 'Number',
            dataIndex: 'storeNumber',
            key: 'storeNumber',
            sorter: true,
            sortOrder: sortedInfo.columnKey === 'storeNumber' ? sortedInfo.order : null,
        },
        {
            title: 'Name',
            dataIndex: 'buildingName',
            key: 'buildingName',
            sorter: true,
            sortOrder: sortedInfo.columnKey === 'storeDisplayName' ? sortedInfo.order : null,
        },
        {
            title: 'Address',
            dataIndex: 'buildingAddress1',
            key: 'buildingAddress1',
        },
        {
            title: 'City',
            dataIndex: 'buildingCity',
            key: 'buildingCity',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_text, record) => (
                <Space>
                    <FormButton title="Details" onClick={() => handleDetails(record)} />
                </Space>
            ),
        },
    ];

    return (
        <>
            {contextHolder}
            <Typography.Title>Stores</Typography.Title>
            <Space style={{ marginBottom: 16 }} wrap>
                <Search
                    placeholder="Search stores by number or name"
                    onSearch={handleSearch}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 300 }}
                    allowClear
                />
                <Button
                    color="primary"
                    variant="filled"
                    icon={<PlusOutlined />}
                    onClick={() => setAddDrawerOpen(true)}
                >
                    Add Store
                </Button>
            </Space>

            {loading ? (
                <Skeleton active />
            ) : (
                <Table
                    dataSource={stores}
                    columns={columns}
                    rowKey="guid"
                    pagination={{
                        current: currentPage,
                        pageSize,
                        total: totalStores,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    onChange={handleTableChange}
                />
            )}
            <StoreDetailsDrawer
                open={detailsDrawerVisible}
                onClose={closeDetailsDrawer}
                storeGuid={selectedStore?.guid ?? ''}
                saveDetails={saveDetails}
                title={selectedStore?.storeNumber}
                form={storeDetailsForm}
                saveLoading={saveLoading}
                activeLanguages={activeLanguages}
                activeCurrencies={activeCurrencies}
            />
            <AddStoreDrawer
                open={addDrawerOpen}
                onClose={() => setAddDrawerOpen(false)}
                onSuccess={() => {
                    setAddDrawerOpen(false);
                    fetchStores();
                }}
                currentUserGuid={createdByGuid}
            />
        </>
    );
};

export default Stores;